/**
 * Making a sheet, from every place that makes one: the Sheets page, Home, the picks
 * tray and a past sheet's "Use as a start".
 *
 * One screen, in the order a tutor thinks about it — what kind of sheet, for which
 * term and number, what it starts from — with format and numbering details folded away
 * under "More options". It submits in one request: the server fills in the text of
 * every exercise, so nothing is created half-way.
 *
 * Where the server has the 2026 course template installed, a new sheet is written
 * against it unless the tutor picks another format: that is the format the course
 * publishes in. Where it has not, nothing changes — offering a template the server
 * cannot build would only produce sheets whose every preview fails.
 *
 * Phase 8, Lane S: a course sheet started from a past sheet with a Typst version in the
 * 2026 archive takes that version — its exercises natively in Typst, its section headings
 * — unless the tutor unticks it; "the same week last year" then prefers the year that has
 * one. A sheet only the archive has can only become a course sheet.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { CourseFunction, Markup, SheetKind, SheetMeta, TermView } from '@studio/shared';
import { COURSE_FUNCTIONS, COURSE_PACK, courseFunctionFor, preferredPastSheet, sheetLabelForWeek, unescapeTex, weekLabel } from '@studio/shared';
import { api, type CorpusSheetSummary, type SheetSummaryWithTerm } from '../api';
import { loadTermView } from '../termView';
import { useBasket } from '../basket';
import { COURSE_FUNCTION_LABELS, englishSheetTypeLabel, L, SHEET_TYPES, sheetTypeLabel } from '../labels';
import { numberingExample } from '../numbering';
import { currentTerm, nextSheetNumber } from '../terms';
import { dateRange, termName } from '../weeks';
import { typstSheetText } from './TypstSheet';
import { usePrefs } from '../prefs';
import { Button, Modal } from './ui';
import { useToast } from './Toast';
import { tx, lc, txn } from '../i18n';

/** `same-week`: the newest past sheet of this series whose number is the week's */
export type StartFrom = 'empty' | 'picks' | 'past' | 'same-week';

export interface NewSheetDefaults {
  startFrom?: StartFrom;
  pastSheetId?: string;
  kind?: SheetKind;
  title?: string;
  /** the term the sheet is for; this term when absent */
  termYear?: string;
  /** the teaching week (Phase 6) */
  week?: number;
  /**
   * The sheet number to start with, for a sheet outside the weekly series (a copy of the
   * end-term preparation sheet is X); a weekly sheet's number follows its week.
   */
  label?: string;
}

/** Week numbers come with a letter only for the two weekly series. */
const WEEKLY_KINDS: readonly SheetKind[] = ['tutorial', 'exercise'];

export { currentTerm, nextSheetNumber } from '../terms';

/** What a new sheet is written in: one of the two markups, or the course template. */
type SheetFormat = Markup | 'course';

/** The course template a sheet of this kind gets when nobody chooses one. */
const defaultCourseFunction = (kind: SheetKind): CourseFunction =>
  courseFunctionFor({ kind } as SheetMeta);

/**
 * A LaTeX sheet for 2026/27 or later starts on the LaTeX 2026 template — the course's look
 * in LaTeX (tools/build-latex2026.mjs) — instead of an older year's pack. Typst stays the
 * default format; this is only what "LaTeX" means for those terms. Kinds the template has
 * no head for keep the old behaviour.
 */
function latex2026Template(kind: SheetKind, term: string): Partial<SheetMeta> {
  if (!(Number(term) >= 2026)) return {};
  const head =
    kind === 'exercise'
      ? { headerName: '2026/exerciseSheetHeader#exercisehead', headCommand: 'exercisehead', sheetType: 'E' }
      : kind === 'exam-prep'
        ? { headerName: '2026/tutorialSheetHeader#klausurtraininghead', headCommand: 'klausurtraininghead', sheetType: 'T' }
        : kind === 'tutorial'
          ? { headerName: '2026/tutorialSheetHeader#tutorialhead', headCommand: 'tutorialhead', sheetType: 'T' }
          : null;
  return head ? { headerYear: '2026', ...head } : {};
}

export function NewSheetForm({
  open,
  onClose,
  defaults = {},
}: {
  open: boolean;
  onClose: () => void;
  defaults?: NewSheetDefaults;
}) {
  const basket = useBasket();
  const { prefs } = usePrefs();
  const navigate = useNavigate();
  const { push } = useToast();

  const [kind, setKind] = useState<SheetKind>(defaults.kind ?? 'tutorial');
  const [term, setTerm] = useState(defaults.termYear ?? currentTerm());
  const [week, setWeek] = useState<number | null>(defaults.week ?? null);
  /** the term's calendar, for the weeks' dates; null: none (weeks are then just numbered) */
  const [cal, setCal] = useState<TermView | null>(null);
  const [label, setLabel] = useState('');
  const [labelTouched, setLabelTouched] = useState(false);
  const [title, setTitle] = useState(defaults.title ?? '');
  const [startFrom, setStartFrom] = useState<StartFrom>(
    defaults.startFrom ?? (basket.ids.length ? 'picks' : 'empty'),
  );
  const [pastId, setPastId] = useState(defaults.pastSheetId ?? '');
  /** a course sheet from a past sheet with a Typst version takes that version (Phase 8) */
  const [useTypst, setUseTypst] = useState(true);
  const [format, setFormat] = useState<SheetFormat>('latex');
  /*
   * Whether the tutor chose the format. The course status arrives after the form opens;
   * it may move the default to the course template, but never over a choice made.
   */
  const formatTouched = useRef(false);
  const [courseAvailable, setCourseAvailable] = useState(false);
  /** null follows the sheet type; a value is a template chosen by hand */
  const [courseFn, setCourseFn] = useState<CourseFunction | null>(null);
  // text, not a number: a number field could not be emptied to type a new value, so
  // typing "3" over "1" gave 13
  const [firstNumberText, setFirstNumberText] = useState('1');
  const firstNumber = Math.max(1, Math.floor(Number(firstNumberText)) || 1);
  const [language, setLanguage] = useState<'en' | 'de'>('en');
  const [more, setMore] = useState(prefs.expandAdvanced);
  const [busy, setBusy] = useState(false);
  const [sheets, setSheets] = useState<SheetSummaryWithTerm[]>([]);
  const [past, setPast] = useState<CorpusSheetSummary[] | null>(null);
  const [pastError, setPastError] = useState<string | null>(null);
  const [pastMissing, setPastMissing] = useState(false);
  const pastSelect = useRef<HTMLSelectElement>(null);

  // reset to the caller's defaults each time the form opens
  useEffect(() => {
    if (!open) return;
    setKind(defaults.kind ?? 'tutorial');
    setTerm(defaults.termYear ?? currentTerm());
    setWeek(defaults.week ?? null);
    setTitle(defaults.title ?? '');
    setStartFrom(defaults.startFrom ?? (basket.ids.length ? 'picks' : 'empty'));
    setPastId(defaults.pastSheetId ?? '');
    setUseTypst(true);
    setLabelTouched(false);
    setPastMissing(false);
    setFirstNumberText('1');
    setCourseFn(null);
    formatTouched.current = false;
    void api.sheets().then(setSheets).catch(() => setSheets([]));
    let live = true;
    void api
      .courseStatus()
      .then((status) => {
        if (!live) return;
        setCourseAvailable(status.available);
        if (!formatTouched.current) setFormat(status.available ? 'course' : 'latex');
      })
      // an older server has no such route; that simply means no course template
      .catch(() => {
        if (!live) return;
        setCourseAvailable(false);
        if (!formatTouched.current) setFormat('latex');
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // the past sheets are needed to start from one, and to name the same week's
  useEffect(() => {
    if (!open || (startFrom !== 'past' && week === null) || past || pastError) return;
    void api
      .corpusSheets()
      .then(setPast)
      .catch((err) => setPastError(err instanceof Error ? err.message : tx('unknown error')));
    // `week` too: a week preset by the caller or picked here is what asks for them
  }, [open, startFrom, week, past, pastError]);

  // the weeks of the chosen term, with their dates when it has a calendar
  useEffect(() => {
    if (!open || !term) {
      setCal(null);
      return;
    }
    let live = true;
    void loadTermView(term)
      .then((view) => live && setCal(view))
      .catch(() => live && setCal(null));
    return () => {
      live = false;
    };
  }, [open, term]);

  /*
   * The number follows term, type and week until the user types one: a tutorial for
   * week 3 is sheet C, as the course has always numbered them (week 0 is sheet 0).
   */
  useEffect(() => {
    if (labelTouched) return;
    const byWeek = week !== null && WEEKLY_KINDS.includes(kind) ? sheetLabelForWeek(week) : null;
    // the caller's number, while the type is still the caller's
    const given = !WEEKLY_KINDS.includes(kind) && kind === defaults.kind ? defaults.label?.trim() : undefined;
    setLabel(byWeek ?? (given || nextSheetNumber(sheets, term, kind)));
  }, [sheets, term, kind, week, labelTouched, defaults.label, defaults.kind]);

  /*
   * A week this term's calendar does not have — "Use as a start" on 2025's sheet O asks
   * for week 15 of a W0–W14 term. The select could not show it and said "no week", but
   * the sheet was made in week 15 all the same. So it really is no week, and the sheet
   * keeps the number it would have had.
   */
  useEffect(() => {
    if (week === null || !cal?.slots.length) return;
    if (cal.slots.some((s) => s.week === week)) return;
    const number = sheetLabelForWeek(week);
    if (number && !labelTouched && WEEKLY_KINDS.includes(kind)) {
      setLabel(number);
      setLabelTouched(true);
    }
    setWeek(null);
  }, [cal, week, labelTouched, kind]);

  /**
   * Last year's sheet of this week, for "Start from: same week last year" — for a course
   * sheet the newest with a Typst version (Phase 8), else the newest.
   */
  const sameWeek = useMemo(() => {
    const weekLabelOf = week !== null ? sheetLabelForWeek(week) : null;
    if (!weekLabelOf || !past) return null;
    const list = past
      .filter((s) => s.kind === kind && s.label.trim().toUpperCase() === weekLabelOf)
      .sort((a, b) => b.year.localeCompare(a.year));
    return preferredPastSheet(list, format === 'course');
  }, [past, kind, week, format]);
  const chosenPastArchiveOnly = Boolean(
    (startFrom === 'past' ? past?.find((s) => s.id === pastId) : startFrom === 'same-week' ? sameWeek : undefined)?.archiveOnly,
  );
  // a week without a past sheet cannot be the start, nor can past sheets that failed to load
  useEffect(() => {
    if (startFrom === 'same-week' && (week === null || ((past || pastError) && !sameWeek))) setStartFrom('empty');
  }, [startFrom, week, past, pastError, sameWeek]);

  // a sheet only the 2026 archive has is Typst for the course headers: offer it as one
  useEffect(() => {
    if (chosenPastArchiveOnly && courseAvailable && !formatTouched.current) setFormat('course');
  }, [chosenPastArchiveOnly, courseAvailable]);

  // suggest the same sheet of the latest past year
  useEffect(() => {
    if (startFrom !== 'past' || pastId || !past) return;
    const same = past
      .filter((s) => s.kind === kind && s.label.toUpperCase() === label.toUpperCase())
      .sort((a, b) => b.year.localeCompare(a.year))[0];
    if (same) setPastId(same.id);
  }, [startFrom, past, kind, label, pastId]);

  const pastByYear = useMemo(() => {
    const map = new Map<string, CorpusSheetSummary[]>();
    for (const s of past ?? []) map.set(s.year, [...(map.get(s.year) ?? []), s]);
    return [...map.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [past]);

  const course = format === 'course';
  /** the past sheet the new one starts from, if any */
  const chosenPast =
    startFrom === 'past' ? past?.find((s) => s.id === pastId) : startFrom === 'same-week' ? sameWeek : undefined;
  // a sheet only the archive has can only be copied as a course sheet
  const archiveOnlyBlocked = Boolean(chosenPast?.archiveOnly) && !course;
  const kindCourseFn = defaultCourseFunction(kind);
  // stored only when it differs from what the type gives, so a later change of type
  // still carries the template along with it
  const chosenCourseFn = course && courseFn && courseFn !== kindCourseFn ? courseFn : undefined;
  const meta: Partial<SheetMeta> & Pick<SheetMeta, 'kind' | 'label'> = {
    kind,
    label: label.trim() || 'A',
    termYear: term || undefined,
    ...(week !== null ? { week } : {}),
    markup: course ? 'typst' : format,
    language,
    numberOffset: Math.max(0, firstNumber - 1),
    // printed on the sheet: course content, English whatever the interface language
    headKindLabel: SHEET_TYPES.some((t) => t.kind === kind) ? englishSheetTypeLabel(kind).replace(' sheet', ' Sheet') : 'Tutorial Sheet',
    ...(course ? { headerName: COURSE_PACK } : {}),
    ...(!course && format === 'latex' ? latex2026Template(kind, term) : {}),
    ...(chosenCourseFn ? { courseFunction: chosenCourseFn } : {}),
  };
  // the same number twice in one term and series is allowed — but rarely meant
  const numberTaken =
    Boolean(label.trim()) &&
    sheets.some(
      (s) => (s.termYear ?? '') === term && s.kind === kind && s.label.toUpperCase() === label.trim().toUpperCase(),
    );
  const fallbackTitle = `${SHEET_TYPES.find((t) => t.kind === kind)?.label ?? 'Sheet'} ${meta.label}`;

  /*
   * "The same week last year" before the past sheets are in: there is nothing to copy yet,
   * and creating would quietly make an empty sheet instead.
   */
  const waitingForSameWeek = startFrom === 'same-week' && !sameWeek;

  const submit = async () => {
    if (waitingForSameWeek || archiveOnlyBlocked) return;
    if (startFrom === 'past' && !pastId) {
      setPastMissing(true);
      pastSelect.current?.focus();
      return;
    }
    setBusy(true);
    try {
      // no title is a valid choice: the printed head already names the sheet
      const t = title.trim();
      const fromPast = startFrom === 'past' ? pastId : startFrom === 'same-week' ? sameWeek?.id : undefined;
      const created =
        fromPast
          ? // a blank title keeps the past sheet's own
            await api.copyCorpusSheet(fromPast, t || undefined, t ? { ...meta, title: t } : meta, { typst: useTypst })
          : await api.createSheet({
              title: t,
              meta: { ...meta, title: t },
              items:
                startFrom === 'picks'
                  ? basket.ids.map((exerciseId) => ({ kind: 'exercise', exerciseId, includeSolution: true }))
                  : [],
              // on the course template, picks with a Typst version in the 2026 archive go in as that
              typst: true,
            });
      if (startFrom === 'picks') basket.clear();
      onClose();
      navigate(`/sheets/${created.id}`);
    } catch (err) {
      push(err instanceof Error ? err.message : tx('Could not create the sheet'), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={L.newSheet}
      wide
      footer={
        <>
          <Button onClick={onClose}>{tx('Cancel')}</Button>
          <Button
            variant="primary"
            type="submit"
            form="new-sheet-form"
            loading={busy || waitingForSameWeek}
            disabled={archiveOnlyBlocked}
          >
            {tx('Create sheet')}
          </Button>
        </>
      }
    >
      <form
        id="new-sheet-form"
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <fieldset>
          <legend className="label mb-1.5">{L.sheetType}</legend>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {SHEET_TYPES.map((t) => (
              <label
                key={t.kind}
                className={clsx(
                  'cursor-pointer rounded-lg border p-2.5 text-sm transition-colors has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-[var(--color-accent-600)]',
                  kind === t.kind
                    ? 'border-[var(--color-accent-600)] bg-[color-mix(in_srgb,var(--color-accent-600)_8%,transparent)]'
                    : 'border-[var(--line)] hover:bg-[var(--surface-muted)]',
                )}
              >
                <input
                  type="radio"
                  name="kind"
                  className="sr-only"
                  checked={kind === t.kind}
                  onChange={() => setKind(t.kind)}
                />
                <span className="block font-medium">{t.label}</span>
                <span className="block text-xs text-[var(--ink-soft)]">{t.hint}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-[7.5rem_9.5rem_6rem_1fr]">
          <label>
            <span className="label">{tx('Term')}</span>
            <select className="input mt-1" value={term} onChange={(e) => setTerm(e.target.value)}>
              {[0, 1, 2].map((d) => {
                const y = String(Number(currentTerm()) - d);
                return (
                  <option key={y} value={y}>
                    {y}/{String(Number(y) + 1).slice(2)}
                  </option>
                );
              })}
              <option value="">{tx('no term')}</option>
            </select>
          </label>
          <label>
            <span className="label">{tx('Week')}</span>
            <select
              className="input mt-1"
              name="week"
              value={week ?? ''}
              onChange={(e) => setWeek(e.target.value === '' ? null : Number(e.target.value))}
            >
              <option value="">{tx('no week')}</option>
              {(cal?.slots.length
                ? cal.slots.filter((s) => s.week !== null)
                : // no calendar: weeks are just numbered, as far as the week given
                  Array.from({ length: Math.max(15, (week ?? 0) + 1) }, (_, w) => ({ week: w, from: '', to: '' }))
              ).map((s) => (
                <option key={s.week} value={s.week!}>
                  {weekLabel(s.week!)}
                  {s.from ? ` · ${dateRange(s.from, s.to)}` : ''}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="label">{L.sheetNumber}</span>
            <input
              className="input mt-1"
              value={label}
              onChange={(e) => {
                setLabel(e.target.value);
                setLabelTouched(true);
              }}
              placeholder="A"
              aria-describedby="new-sheet-numbering"
            />
          </label>
          <label className="col-span-2 sm:col-span-1">
            <span className="label">{tx('Title')}</span>
            <input
              className="input mt-1"
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={tx('e.g. Lists and Folds (optional)')}
            />
          </label>
        </div>
        <p id="new-sheet-numbering" className="-mt-3 text-xs text-[var(--ink-soft)]">
          {tx('Exercises will be numbered {example}', { example: numberingExample({ ...meta, sheetType: undefined }, firstNumber) })}
          {numberTaken && (
            <span className="ml-2 text-[var(--color-camel-600)]">
              {tx('· this term already has {sheet}', { sheet: `${lc(sheetTypeLabel(kind))} ${meta.label}` })}
            </span>
          )}
        </p>

        <fieldset>
          <legend className="label mb-1.5">{tx('Start from')}</legend>
          <div className="space-y-1.5">
            <Choice checked={startFrom === 'empty'} onChange={() => setStartFrom('empty')} title={tx('An empty sheet')}>
              {tx('Add exercises from the {exercises} page once it is open.', { exercises: L.exercises })}
            </Choice>
            <Choice
              checked={startFrom === 'picks'}
              onChange={() => setStartFrom('picks')}
              disabled={!basket.ids.length}
              title={`${L.picks} (${basket.ids.length})`}
            >
              {basket.ids.length
                ? tx('The exercises you collected, in their order. Your picks are emptied afterwards.')
                : tx('You have not collected any exercises yet.')}
            </Choice>
            {week !== null && (
              <Choice
                checked={startFrom === 'same-week'}
                onChange={() => setStartFrom('same-week')}
                disabled={Boolean(past || pastError) && !sameWeek}
                title={
                  // the newest year that has the week, which is not always last year
                  sameWeek && sameWeek.year !== String(Number(term) - 1)
                    ? tx('The same week in an earlier year')
                    : tx('The same week last year')
                }
              >
                {sameWeek
                  ? tx('{sheet} of {term}{title} · {count}. Copied like a past sheet.', {
                      sheet: `${sheetTypeLabel(sameWeek.kind)} ${sameWeek.label}`,
                      term: termName(sameWeek.year),
                      title: unescapeTex(sameWeek.title) ? `: ${unescapeTex(sameWeek.title)}` : '',
                      count: txn(sameWeek.exerciseCount, '{n} exercise', '{n} exercises'),
                    })
                  : past
                    ? tx('There is no past {type} for {week}.', { type: lc(sheetTypeLabel(kind)), week: weekLabel(week) })
                    : pastError
                      ? tx('The past sheets could not be loaded ({pastError}).', { pastError })
                      : tx('Looking for last year’s sheet of this week…')}
              </Choice>
            )}
            <Choice checked={startFrom === 'past'} onChange={() => setStartFrom('past')} title={tx('A sheet from a past term')}>
              {tx('Copies its exercises and its kNobel teaser; you can change everything afterwards.')}
            </Choice>
            {startFrom === 'past' &&
              (pastError ? (
                <p className="ml-6 text-xs text-[var(--color-danger-500)]">
                  {tx('The past sheets could not be loaded ({pastError}).', { pastError })}{' '}
                  <button type="button" className="underline" onClick={() => setPastError(null)}>
                    {tx('Try again')}
                  </button>
                </p>
              ) : past && past.length === 0 ? (
                <p className="ml-6 text-xs text-[var(--ink-soft)]">{tx('There are no past sheets to start from.')}</p>
              ) : (
                <>
                  <select
                    ref={pastSelect}
                    className="input ml-6 w-[calc(100%-1.5rem)]"
                    value={pastId}
                    onChange={(e) => {
                      setPastId(e.target.value);
                      setPastMissing(false);
                    }}
                    aria-label={tx('Past sheet to start from')}
                    aria-invalid={pastMissing}
                    aria-describedby={pastMissing ? 'new-sheet-past-missing' : undefined}
                  >
                    <option value="">{past ? tx('Choose a sheet…') : tx('Loading…')}</option>
                    {pastByYear.map(([year, list]) => (
                      <optgroup key={year} label={year}>
                        {list.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.label} — {unescapeTex(s.title) || tx('untitled')} · {sheetTypeLabel(s.kind)} ·{' '}
                            {txn(s.exerciseCount, '{n} exercise', '{n} exercises')}
                            {s.typst ? ` · ${L.typstVersion}` : ''}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  {pastMissing && (
                    <p id="new-sheet-past-missing" className="ml-6 text-xs text-[var(--color-danger-500)]">
                      {tx('Choose the sheet to start from.')}
                    </p>
                  )}
                </>
              ))}
            {chosenPast?.typst && course && !chosenPast.archiveOnly && (
              <label className="ml-6 flex items-start gap-2 rounded-md bg-[color-mix(in_srgb,var(--color-teal-500)_8%,transparent)] p-2 text-xs" data-testid="use-typst-version">
                <input type="checkbox" className="mt-0.5" checked={useTypst} onChange={(e) => setUseTypst(e.target.checked)} />
                <span>
                  <span className="font-medium">{tx('Use the Typst version (2026 archive)')}</span>
                  <span className="block text-[var(--ink-soft)]">{typstSheetText(chosenPast.typst)}</span>
                  {useTypst && (
                    <span className="block text-[var(--ink-soft)]" data-testid="typst-no-knobel">
                      {tx('A kNobel teaser is not copied: “Add a block” → “kNobel teaser” adds one in Typst.')}
                    </span>
                  )}
                </span>
              </label>
            )}
            {chosenPast?.archiveOnly && (
              <p className={clsx('ml-6 text-xs', archiveOnlyBlocked ? 'text-[var(--color-danger-500)]' : 'text-[var(--ink-soft)]')} data-testid="archive-only-note">
                {archiveOnlyBlocked
                  ? courseAvailable
                    ? tx('This sheet exists only in the 2026 Typst archive: choose the course template under More options to start from it.')
                    : tx('This sheet exists only in the 2026 Typst archive, and this server has no course template to start a sheet from it.')
                  : tx('This sheet exists only in the 2026 Typst archive: the new sheet is on the course template.')}
              </p>
            )}
          </div>
        </fieldset>

        <div>
          <button
            type="button"
            className="flex items-center gap-1 text-xs font-medium text-[var(--ink-soft)] hover:text-[var(--ink)]"
            aria-expanded={more}
            onClick={() => setMore((m) => !m)}
          >
            {more ? <ChevronDown size={14} /> : <ChevronRight size={14} />}{' '}{tx('More options')}
            {/* the format is folded away too, but it decides the most; it stays visible */}
            <span className="font-normal text-[var(--ink-faint)]">
              · {course ? L.courseTemplate : format === 'typst' ? 'Typst' : 'LaTeX'}
            </span>
          </button>
          {more && (
            <div className="mt-2 grid gap-3 sm:grid-cols-3">
              <label>
                <span className="label">{tx('Format')}</span>
                <select
                  className="input mt-1"
                  name="format"
                  value={format}
                  onChange={(e) => {
                    formatTouched.current = true;
                    setFormat(e.target.value as SheetFormat);
                  }}
                >
                  {(courseAvailable || course) && <option value="course">{L.courseTemplate} (Typst)</option>}
                  <option value="latex">{tx('LaTeX (classic)')}</option>
                  <option value="typst">Typst</option>
                </select>
              </label>
              {course && (
                <label>
                  <span className="label">{tx('Template')}</span>
                  <select
                    className="input mt-1"
                    name="courseFunction"
                    value={courseFn ?? kindCourseFn}
                    onChange={(e) => {
                      const next = e.target.value as CourseFunction;
                      setCourseFn(next === kindCourseFn ? null : next);
                    }}
                  >
                    {COURSE_FUNCTIONS.map((fn) => (
                      <option key={fn} value={fn}>
                        {COURSE_FUNCTION_LABELS[fn]}
                        {fn === kindCourseFn ? tx(' (from the type)') : ''}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label>
                <span className="label">{tx('First exercise number')}</span>
                <input
                  className="input mt-1"
                  inputMode="numeric"
                  value={firstNumberText}
                  onChange={(e) => setFirstNumberText(e.target.value.replace(/[^0-9]/g, ''))}
                  onBlur={() => setFirstNumberText(String(firstNumber))}
                />
              </label>
              <label>
                <span className="label">{tx('Language')}</span>
                <select className="input mt-1" value={language} onChange={(e) => setLanguage(e.target.value as 'en' | 'de')}>
                  <option value="en">{tx('English')}</option>
                  <option value="de">{tx('German')}</option>
                </select>
              </label>
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
}

function Choice({
  checked,
  onChange,
  disabled,
  title,
  children,
}: {
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <label className={clsx('flex cursor-pointer items-start gap-2 text-sm', disabled && 'cursor-not-allowed opacity-50')}>
      <input type="radio" name="start" className="mt-1" checked={checked} onChange={onChange} disabled={disabled} />
      <span>
        <span className="font-medium">{title}</span>
        <span className="block text-xs text-[var(--ink-soft)]">{children}</span>
      </span>
    </label>
  );
}
